/** @jsx jsx */
import React, { useCallback, useEffect, useMemo } from 'react';
import { jsx } from '@emotion/core';
import { map, sortBy, pipe, prop } from 'ramda';
import { Divider, Box } from '@material-ui/core';

import styles from './styles';
import { helpText } from 'features/Theme';
import ExpandablePanel from 'components/ExpandablePanel';
import BreadTitle from './BreadTitle';

import useTranslate, {
  useMarkdownTranslate,
} from 'hooks/useTranslate/useTranslate';
import { InstallmentsInitialOffer } from 'state/initialFinanceOffers';
import { monthlyPayment } from 'utils/finance';
import { conformToMask, decimalMoneyMask } from 'utils/masks';
import { useSelector } from 'react-redux';
import { selectTenant } from 'state/tenant';
import useAnalytics, { Event } from 'hooks/useAnalytics';

export interface InstallmentPaymentOptionProps {
  intendedPurchaseAmount: number;
  installmentOffers: InstallmentsInitialOffer[];
  panelName: string;
  expanded: boolean;
  onChange: React.ComponentProps<typeof ExpandablePanel>['onChange'];
}

interface InstallmentOfferData {
  id: string;
  perMonth: number;
  termLength: number;
  apr: number;
  total: number;
  interest: number;
}

const getInstallmentOffersData = (
  principal: number,
  rateBps: number,
  offers: InstallmentsInitialOffer[],
): InstallmentOfferData[] => {
  const installmentOffersData: InstallmentOfferData[] = pipe(
    map((financeOffer: InstallmentsInitialOffer) => {
      // Per Bread, use the display rate if maxRate is non-zero
      const apr = financeOffer.maxRate !== 0 ? rateBps / 100 / 100 : 0;
      const termLength = financeOffer.termLength;
      const perMonth = monthlyPayment(principal, apr, termLength);
      const total = perMonth * termLength;
      const interest = total - principal;
      return {
        id: financeOffer.id,
        perMonth,
        termLength,
        apr,
        total,
        interest,
      };
    }),
    sortBy(prop('termLength')),
  )(offers);

  return installmentOffersData;
};

const Installments: React.FC<InstallmentPaymentOptionProps> = props => {
  const {
    panelName,
    expanded,
    intendedPurchaseAmount,
    installmentOffers,
    onChange,
  } = props;

  const { sendEvents } = useAnalytics();

  useEffect(() => {
    expanded && sendEvents([Event.InstallmentInitialOfferExpanded]);
  }, [expanded, sendEvents]);

  const { bread } = useSelector(selectTenant);

  if (!bread) {
    throw new Error('Invalid tenant');
  }

  const installmentOffersData = useMemo(
    () =>
      getInstallmentOffersData(
        intendedPurchaseAmount,
        // If we are display Instalmment offer, displayInterestRateBps must
        // have been configured.
        bread.displayInterestRateBps as number,
        installmentOffers,
      ),
    [bread.displayInterestRateBps, installmentOffers, intendedPurchaseAmount],
  );

  const minPayment = useMemo(
    () => Math.min(...map(prop('perMonth'), installmentOffersData)),
    [installmentOffersData],
  );

  const t = useTranslate();
  const tMd = useMarkdownTranslate();

  const maxCapacity = useMemo(() => {
    const result = Math.max(...map(prop('maxCartSize'), installmentOffers));
    return result
      ? {
          maxCapacityString: conformToMask(
            (result / 100).toFixed(2),
            decimalMoneyMask,
          ),
          maxCapacityNumber: result / 100,
        }
      : undefined;
  }, [installmentOffers]);

  const minCapacity = useMemo(() => {
    const result = Math.max(...map(prop('minCartSize'), installmentOffers));
    return result
      ? {
          minCapacityString: conformToMask(
            (result / 100).toFixed(2),
            decimalMoneyMask,
          ),
          minCapacityNumber: result / 100,
        }
      : undefined;
  }, [installmentOffers]);

  const inErrorOver = useMemo(() => {
    return (
      maxCapacity && intendedPurchaseAmount > maxCapacity.maxCapacityNumber
    );
  }, [intendedPurchaseAmount, maxCapacity]);

  const inErrorUnder = useMemo(() => {
    return (
      minCapacity && intendedPurchaseAmount < minCapacity.minCapacityNumber
    );
  }, [intendedPurchaseAmount, minCapacity]);

  const bannerSecondaryText = useMemo(() => {
    if (maxCapacity && inErrorOver) {
      return tMd('unified.offer.intendedPurchaseAmountMaximumError', {
        amount: conformToMask(
          (intendedPurchaseAmount - maxCapacity.maxCapacityNumber).toFixed(2),
          decimalMoneyMask,
        ),
      });
    }
    if (minCapacity && inErrorUnder) {
      return tMd('unified.offer.intendedPurchaseAmountMinimumError', {
        amount: conformToMask(
          (minCapacity.minCapacityNumber - intendedPurchaseAmount).toFixed(2),
          decimalMoneyMask,
        ),
      });
    }

    return null;
  }, [
    inErrorOver,
    inErrorUnder,
    intendedPurchaseAmount,
    maxCapacity,
    minCapacity,
    tMd,
  ]);

  const handlePanelExpandChangeWhenNotError = useCallback(
    (event: React.ChangeEvent<{}>, panelName: string, expanded: boolean) => {
      if (!inErrorOver && !inErrorUnder) {
        if (onChange) {
          onChange(event, panelName, expanded);
        }
      }
    },
    [inErrorOver, inErrorUnder, onChange],
  );

  const title = (
    <div css={styles.paymentOptionTitleText}>
      <BreadTitle
        titlePart={tMd('unified.offerIntro.paymentOption.installments.title')}
        logo={t('unified.offerIntro.paymentOption.installments.breadLogo')}
        id={'installmentsBreadLogo'}
      />
      {inErrorOver || inErrorUnder ? null : (
        <div css={styles.paymentOptionText}>
          {tMd('unified.offerIntro.paymentOption.installments.description', {
            amount: `${conformToMask(
              minPayment.toFixed(2),
              decimalMoneyMask,
            )}/mo*`,
          })}
        </div>
      )}
    </div>
  );

  const body = (
    <div css={styles.paymentOptionText}>
      <div css={styles.divider}>
        <Divider />
      </div>
      <div css={styles.installmentPlanMessage}>
        <span css={helpText}>
          {tMd('unified.offerIntro.paymentOption.installments.planMessage')}
        </span>
      </div>
      {installmentOffersData.map((installmentOffer: InstallmentOfferData) => {
        return (
          <Box key={installmentOffer.id} css={styles.installmentOffer}>
            <span css={styles.bigMoney}>
              {conformToMask(
                installmentOffer.perMonth.toFixed(2),
                decimalMoneyMask,
              )}
            </span>
            <span css={helpText}>/mo</span>
            <div>
              for {installmentOffer.termLength}{' '}
              {installmentOffer.termLength > 1 ? 'months' : 'month'}
              <span css={{ float: 'right' }}>
                {(installmentOffer.apr * 100).toFixed(2)}% APR
              </span>
            </div>
            <div>
              Interest:{' '}
              {conformToMask(
                installmentOffer.interest.toFixed(2),
                decimalMoneyMask,
              )}
              <span css={{ float: 'right' }}>
                Total:{' '}
                {conformToMask(
                  installmentOffer.total.toFixed(2),
                  decimalMoneyMask,
                )}
              </span>
            </div>
          </Box>
        );
      })}
      <div>
        <span css={helpText}>
          <span css={styles.semiBold}>
            {tMd(
              'unified.offerIntro.paymentOption.installments.planAvailability',
            )}
          </span>
        </span>
      </div>
    </div>
  );
  return (
    <ExpandablePanel
      expanded={inErrorOver || inErrorUnder ? false : expanded}
      hideToggleIcon={inErrorOver || inErrorUnder}
      panelName={panelName}
      title={title}
      children={body}
      onChange={handlePanelExpandChangeWhenNotError}
      bannerSecondaryText={bannerSecondaryText}
    />
  );
};

export default Installments;
